
import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';

const BlogPage = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [activeCategory, setActiveCategory] = useState('All');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const categories = ['All', 'Business', 'E-commerce', 'Wellness', 'Personal Growth'];

  const featuredPost = {
    title: 'The Power of Balancing Business Growth with Personal Wellbeing',
    excerpt: 'Discover why the most successful entrepreneurs prioritize their health and how you can achieve sustainable growth without burnout.',
    category: 'Wellness',
    image: 'https://public.readdy.ai/ai/img_res/af939aff90589c87366818d06197b9d3.jpg',
    author: 'Titilayo Ajayi',
    date: 'Jan 15, 2025',
    readTime: '8 min read',
  };

  const blogPosts = [
    {
      title: '10 E-commerce Strategies That Actually Work in 2025',
      excerpt: 'Learn the proven tactics to boost your online store conversions and increase sales in today\'s competitive market.',
      category: 'E-commerce',
      image: 'https://readdy.ai/api/search-image?query=modern%20online%20shopping%20experience%20on%20laptop%20screen%20showing%20ecommerce%20website%20with%20products%20and%20shopping%20cart%2C%20clean%20interface%20design%20with%20conversion%20optimization%20elements%2C%20professional%20workspace%20highlighting%20digital%20retail%20success%20strategies&width=600&height=400&seq=blog-ecom-1&orientation=landscape',
      date: 'Jan 12, 2025',
      readTime: '6 min read',
    },
    {
      title: 'How to Create a Winning Business Strategy from Scratch',
      excerpt: 'A step-by-step guide to developing a comprehensive business strategy that drives growth and profitability.',
      category: 'Business',
      image: 'https://readdy.ai/api/search-image?query=business%20strategy%20planning%20session%20with%20charts%20and%20graphs%20on%20whiteboard%2C%20professional%20team%20brainstorming%20in%20modern%20office%2C%20strategic%20planning%20documents%20and%20laptops%20visible%2C%20collaborative%20entrepreneurial%20atmosphere&width=600&height=400&seq=blog-biz-1&orientation=landscape',
      date: 'Jan 10, 2025',
      readTime: '7 min read',
    },
    {
      title: 'Morning Rituals of Highly Successful Entrepreneurs',
      excerpt: 'Discover the morning habits that set high-performing business leaders apart and how to implement them yourself.',
      category: 'Wellness',
      image: 'https://readdy.ai/api/search-image?query=peaceful%20morning%20routine%20of%20successful%20entrepreneur%2C%20person%20doing%20yoga%20or%20meditation%20at%20sunrise%20with%20journal%20and%20healthy%20breakfast%2C%20bright%20natural%20setting%20with%20plants%20promoting%20wellness%20and%20productivity%20habits&width=600&height=400&seq=blog-wellness-1&orientation=landscape',
      date: 'Jan 8, 2025',
      readTime: '5 min read',
    },
    {
      title: 'Scaling Your Online Store: A Complete Roadmap',
      excerpt: 'Everything you need to know about taking your e-commerce business to the next level.',
      category: 'E-commerce',
      image: 'https://readdy.ai/api/search-image?query=growing%20online%20business%20dashboard%20showing%20upward%20sales%20trends%20and%20analytics%20graphs%2C%20ecommerce%20scaling%20metrics%20on%20computer%20screen%2C%20professional%20workspace%20with%20packages%20and%20inventory%20management%20system&width=600&height=400&seq=blog-ecom-2&orientation=landscape',
      date: 'Jan 5, 2025',
      readTime: '9 min read',
    },
    {
      title: 'Overcoming Entrepreneurial Burnout: A Personal Story',
      excerpt: 'My journey through burnout and the lessons I learned about sustainable business practices.',
      category: 'Personal Growth',
      image: 'https://readdy.ai/api/search-image?query=entrepreneur%20recovering%20from%20burnout%20finding%20peace%20and%20balance%2C%20person%20relaxing%20in%20natural%20outdoor%20setting%2C%20journey%20from%20stress%20to%20wellness%2C%20hopeful%20atmosphere%20showing%20personal%20transformation%20and%20resilience&width=600&height=400&seq=blog-growth-1&orientation=landscape',
      date: 'Jan 3, 2025',
      readTime: '10 min read',
    },
    {
      title: 'Financial Planning for Small Business Owners',
      excerpt: 'Essential financial strategies to ensure your business remains profitable and sustainable.',
      category: 'Business',
      image: 'https://readdy.ai/api/search-image?query=small%20business%20financial%20planning%20with%20calculator%20spreadsheets%20and%20charts%2C%20professional%20workspace%20showing%20budget%20management%20and%20profit%20analysis%2C%20organized%20desk%20with%20financial%20documents%20and%20laptop&width=600&height=400&seq=blog-biz-2&orientation=landscape',
      date: 'Dec 30, 2024',
      readTime: '8 min read',
    },
  ];

  const filteredPosts = activeCategory === 'All' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === activeCategory);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setSubmitMessage('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d5p68ido3ikh5o9dul60', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Successfully subscribed!');
        setEmail('');
      } else {
        setSubmitMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitMessage(''), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-20">
        {/* Hero */}
        <section ref={heroRef} className="py-24 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <h1 className="font-serif font-bold text-5xl lg:text-6xl text-primary-charcoal mb-6">
                Insights & Resources
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                Business strategies, e-commerce tips, and wellness wisdom
              </p>

              <div className="flex flex-wrap items-center justify-center gap-3">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setActiveCategory(category)}
                    className={`px-6 py-2 rounded-full font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                      activeCategory === category
                        ? 'bg-primary-gold text-white shadow-lg'
                        : 'bg-primary-beige text-primary-charcoal hover:bg-accent-light-sage'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Featured Post */}
        <section className="pb-24 px-6 lg:px-12">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="grid lg:grid-cols-2 gap-8 bg-primary-beige rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300"
            >
              <div className="relative h-80 lg:h-auto">
                <img
                  src={featuredPost.image}
                  alt={featuredPost.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-6 left-6 bg-primary-gold text-white px-4 py-2 rounded-full text-sm font-semibold">
                  Featured
                </div>
              </div>

              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <div className="inline-flex items-center space-x-2 bg-accent-light-sage px-3 py-1 rounded-full text-sm font-medium text-primary-sage mb-4 w-fit">
                  {featuredPost.category}
                </div>

                <h2 className="font-serif font-bold text-3xl lg:text-4xl text-primary-charcoal mb-4">
                  {featuredPost.title}
                </h2>

                <p className="text-gray-600 leading-relaxed mb-6">
                  {featuredPost.excerpt}
                </p>

                <div className="flex items-center space-x-4 text-sm text-gray-500 mb-6">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-primary-sage rounded-full text-white text-xs font-bold">
                      TA
                    </div>
                    <span>By {featuredPost.author}</span>
                  </div>
                  <span>•</span>
                  <span>{featuredPost.date}</span>
                  <span>•</span>
                  <div className="flex items-center space-x-1">
                    <i className="ri-time-line"></i>
                    <span>{featuredPost.readTime}</span>
                  </div>
                </div>

                <a
                  href="#"
                  className="inline-flex items-center text-primary-gold hover:text-accent-warm-gold font-semibold transition-colors cursor-pointer whitespace-nowrap w-fit"
                >
                  Read Article
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Blog Grid */}
        <section className="pb-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <BlogCard key={index} post={post} index={index} />
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="py-24 bg-gradient-to-br from-primary-sage to-primary-gold">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center text-white">
            <i className="ri-mail-send-line text-6xl mb-6"></i>
            <h2 className="font-serif font-bold text-4xl lg:text-5xl mb-6">
              Get Weekly Insights Delivered
            </h2>
            <p className="text-xl mb-10">
              Join our newsletter for exclusive tips on business growth, e-commerce, and wellness
            </p>
            
            <form onSubmit={handleNewsletterSubmit} id="blog-newsletter" data-readdy-form className="max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 px-6 py-4 rounded-lg text-primary-charcoal placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white"
                  required
                  disabled={isSubmitting}
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-8 py-4 bg-primary-charcoal hover:bg-primary-charcoal/90 text-white font-semibold rounded-lg transition-all duration-300 cursor-pointer disabled:opacity-50 whitespace-nowrap"
                >
                  Subscribe
                </button>
              </div>
              {submitMessage && (
                <p className={`mt-3 text-sm ${submitMessage.includes('Success') ? 'text-white' : 'text-red-200'}`}>
                  {submitMessage}
                </p>
              )}
            </form>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

const BlogCard = ({ post, index }: { post: any; index: number }) => {
  return (
    <motion.article
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      whileHover={{ y: -8 }}
      className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group"
    >
      <div className="relative h-56 overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute bottom-4 left-4 bg-white px-3 py-1 rounded-full text-xs font-semibold text-primary-charcoal">
          {post.category}
        </div>
      </div>

      <div className="p-6">
        <h3 className="font-serif font-bold text-xl text-primary-charcoal mb-3 line-clamp-2 hover:text-primary-gold transition-colors cursor-pointer">
          {post.title}
        </h3>

        <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
          {post.excerpt}
        </p>

        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center space-x-1">
            <i className="ri-time-line"></i>
            <span>{post.readTime}</span>
          </div>
          <span>{post.date}</span>
        </div>
      </div>
    </motion.article>
  );
};

export default BlogPage;
